import React, { useState } from 'react';
import styled from '@emotion/styled';
import { DashboardAuditSection, DashboardWeek } from '../models/dashboard';
import {
  CRITICAL_SCORE,
  LocationWeekPoints,
  METRIC_FILL,
  METRIC_LINE,
  Metric,
  locationHistory,
  weekRange,
  weeksAgoLabel,
} from '../lib/dashboard';
import { WeekSelector } from './DashboardView';
import AuditSection from './AuditSection';

// Weekly dashboard (GRI-957): one location's report, as in the design prototype, which follows
// the v1 weekly report (location.hbs): score gauge, trend of points per metric over up to 8
// reported weeks (newest on the left) and the points of each metric ("By category").

type Props = {
  weeks: DashboardWeek[]; // newest first
  selected: number;
  onSelect: (index: number) => void;
  locationId: string;
  onBack: () => void;
  today?: Date;
  // The audit samples of the selected week (query dashboardLocationSections); undefined while loading.
  sections?: DashboardAuditSection[];
};

const Gauge = ({ score }: { score: number | null }) => (
  <GaugeBox>
    <svg
      viewBox="0 0 320 256"
      role="img"
      aria-label={
        score === null ? 'No score this week' : `Score ${score} out of 100`
      }
    >
      <path
        d="M 20 208 A 140 140 0 0 1 300 208"
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={40}
      />
      {score !== null && (
        <path
          d="M 20 208 A 140 140 0 0 1 300 208"
          fill="none"
          stroke={score < CRITICAL_SCORE ? '#DC2626' : '#1D4ED8'}
          strokeWidth={40}
          pathLength={100}
          strokeDasharray={`${score} 100`}
        />
      )}
    </svg>
    <div className="value">
      <span className="score">{score === null ? '—' : score}</span>
      <span className="of">/100</span>
    </div>
  </GaugeBox>
);

// Stacked points per metric, bottom-up in metric order; Y fixed 0-100 like the v1 chart.
const Trend = ({
  metrics,
  history,
  today,
}: {
  metrics: Metric[];
  history: LocationWeekPoints[];
  today?: Date;
}) => {
  const [tip, setTip] = useState<{ k: number; j: number } | null>(null);
  const n = history.length;
  const rotate = n > 3;
  const L = 34,
    R = 310,
    T = 8,
    B = rotate ? 190 : 230;
  const xAt = (j: number) => (n <= 1 ? L : L + (j * (R - L)) / (n - 1));
  const yAt = (v: number) => T + ((100 - v) / 100) * (B - T);
  const labels = history.map((h) => weeksAgoLabel(h.weekStart, today));
  let base = history.map(() => 0);
  const layers = metrics.map((m, k) => {
    const lo = base;
    const hi = history.map((h, j) => lo[j] + (h.points[m.auditId] || 0));
    base = hi;
    return { k, m, lo, hi };
  });
  const pts = (vals: number[]) => vals.map((v, j) => `${xAt(j)},${yAt(v)}`);
  const pctX = (x: number) => `${(x / 320) * 100}%`;
  const pctY = (y: number) => `${(y / 256) * 100}%`;
  const active = tip ? layers[tip.k] : null;

  return (
    <ChartBox>
      <svg viewBox="0 0 320 256" role="img" aria-label="Score trend by metric">
        {Array.from({ length: 11 }, (_, s) => (
          <line
            key={s}
            x1={L}
            x2={R}
            y1={yAt(100 - s * 10)}
            y2={yAt(100 - s * 10)}
            stroke="rgba(0,0,0,0.1)"
            strokeWidth={1}
          />
        ))}
        {history.map((h, j) => (
          <line
            key={h.weekStart}
            x1={xAt(j)}
            x2={xAt(j)}
            y1={T}
            y2={B + 5}
            stroke="rgba(0,0,0,0.1)"
            strokeWidth={1}
          />
        ))}
        {layers.map(({ k, lo, hi }) => (
          <polygon
            key={`a${k}`}
            points={pts(hi).concat(pts(lo).reverse()).join(' ')}
            fill={METRIC_FILL[k % METRIC_FILL.length]}
          />
        ))}
        {layers.map(({ k, hi }) => (
          <g key={`l${k}`}>
            <polyline
              points={pts(hi).join(' ')}
              fill="none"
              stroke={METRIC_LINE[k % METRIC_LINE.length]}
              strokeWidth={3}
              strokeLinejoin="round"
            />
            {hi.map((v, j) => (
              <g key={j}>
                <circle
                  cx={xAt(j)}
                  cy={yAt(v)}
                  r={3}
                  fill={METRIC_FILL[k % METRIC_FILL.length]}
                  stroke={METRIC_LINE[k % METRIC_LINE.length]}
                  strokeWidth={1}
                />
                <circle
                  data-testid={`trend-point-${k}-${j}`}
                  cx={xAt(j)}
                  cy={yAt(v)}
                  r={9}
                  fill="transparent"
                  onMouseEnter={() => setTip({ k, j })}
                  onMouseLeave={() => setTip(null)}
                />
              </g>
            ))}
          </g>
        ))}
      </svg>
      {Array.from({ length: 11 }, (_, s) => (
        <Axis
          key={`y${s}`}
          style={{
            left: pctX(L - 6),
            top: pctY(yAt(100 - s * 10)),
            transform: 'translate(-100%, -50%)',
            fontSize: 12,
          }}
        >
          {100 - s * 10}
        </Axis>
      ))}
      {labels.map((label, j) => (
        <Axis
          key={`x${j}`}
          style={{
            left: pctX(xAt(j)),
            top: pctY(B + 10),
            fontSize: 10,
            transform: rotate
              ? 'translate(-100%, 0) rotate(-45deg)'
              : 'translate(-50%, 0)',
            transformOrigin: rotate ? '100% 0' : undefined,
          }}
        >
          {label}
        </Axis>
      ))}
      {tip && active && (
        <Tip
          role="tooltip"
          style={{ left: pctX(xAt(tip.j)), top: pctY(yAt(active.hi[tip.j])) }}
        >
          <div style={{ fontWeight: 700 }}>{labels[tip.j]}</div>
          <div>
            {active.m.name}:{' '}
            {Math.round((history[tip.j].points[active.m.auditId] || 0) * 10) /
              10}
          </div>
        </Tip>
      )}
    </ChartBox>
  );
};

const DashboardLocationView = ({
  weeks,
  selected,
  onSelect,
  locationId,
  onBack,
  today,
  sections,
}: Props) => {
  const week = weeks[selected];
  const location = week.locations.find((l) => l.locationId === locationId);
  const name =
    location?.name ||
    weeks.flatMap((w) => w.locations).find((l) => l.locationId === locationId)
      ?.name ||
    locationId;
  const { metrics, history } = locationHistory(weeks, selected, locationId);
  const now =
    history.length && history[0].weekStart === week.weekStart
      ? history[0]
      : null;
  const ago = weeksAgoLabel(week.weekStart, today);
  // Audit sections in the "By category" order.
  const order = (id: string) => {
    const i = metrics.findIndex((m) => m.auditId === id);
    return i < 0 ? metrics.length : i;
  };
  const ordered = [...(sections || [])].sort(
    (a, b) => order(a.auditId) - order(b.auditId)
  );

  return (
    <Page>
      <Head>
        <button
          className="back"
          aria-label="Back to all locations"
          title="Back to all locations"
          onClick={onBack}
        >
          ←
        </button>
        <h1>Location {name}</h1>
      </Head>
      <div style={{ marginTop: 16 }}>
        <WeekSelector
          weeks={weeks}
          selected={selected}
          onSelect={onSelect}
          today={today}
        />
      </div>

      {!now ? (
        <Empty>
          No audits at this location for the week of {weekRange(week)}.
        </Empty>
      ) : (
        <Score>
          <div className="col">
            <h3>
              {ago === 'This week'
                ? 'Score for this week'
                : `Score for the week of ${weekRange(week)}`}
            </h3>
            <Gauge score={now.score} />
          </div>
          <div className="pair">
            <div className="col">
              <h3 className="trend-title">Trend</h3>
              <Trend metrics={metrics} history={history} today={today} />
            </div>
            <div className="col">
              <h3 className="cat-title">By category</h3>
              <ul>
                {metrics.map((m, k) => (
                  <li key={m.auditId}>
                    <div className="label">
                      <a className="name" href={`#audit-${m.auditId}`}>
                        {m.name}
                      </a>
                      <div className="meta">
                        <span
                          className="swatch"
                          style={{
                            background: METRIC_LINE[k % METRIC_LINE.length],
                          }}
                        />
                        <span>
                          {m.category} · out of: {Math.round(m.maxPoints)}
                        </span>
                      </div>
                    </div>
                    <div className="pts">
                      {m.auditId in now.points
                        ? Math.round(now.points[m.auditId])
                        : '—'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Score>
      )}
      {now && sections === undefined && <Empty>Loading the audits…</Empty>}
      {now &&
        ordered.map((s) => (
          <AuditSection
            key={s.auditId}
            section={s}
            audit={location?.audits.find((a) => a.auditId === s.auditId)}
            weekStart={week.weekStart}
          />
        ))}
    </Page>
  );
};

const Page = styled.div`
  max-width: 1152px;
  margin: 0 auto;
  padding: 24px 60px 60px;
  color: #191c20;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-variant-numeric: tabular-nums;
  @media (max-width: 640px) {
    padding: 8px 16px 40px;
  }
`;

const Head = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 -8px;
  padding: 8px;
  background: #f8f9ff;
  border-bottom: 1px solid #c3c6cf;
  .back {
    flex: none;
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: none;
    font-size: 24px;
    color: #191c20;
    cursor: pointer;
  }
  h1 {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 28px;
    font-weight: 400;
    line-height: 36px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: 640px) {
    h1 {
      font-size: 22px;
      line-height: 28px;
    }
  }
`;

const Empty = styled.p`
  margin-top: 32px;
  color: #73777f;
`;

// v1 report look: system font, gray text, 16px.
const Score = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 32px 48px;
  margin-top: 24px;
  color: #1f2937;
  font-family: ui-sans-serif, system-ui, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  h3 {
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 600;
  }
  .col {
    flex: 1 1 320px;
    min-width: 0;
  }
  .pair {
    flex: 2 1 560px;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 32px 48px;
  }
  .trend-title {
    margin-left: 56px;
  }
  .cat-title {
    text-align: right;
    margin-right: 72px;
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .name {
    display: block;
    color: inherit;
    text-decoration: none;
  }
  .name:hover {
    text-decoration: underline;
  }
  .label {
    flex: 1;
    min-width: 0;
    text-align: right;
  }
  .meta {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
    font-size: 14px;
    line-height: 20px;
    color: #6b7280;
  }
  .swatch {
    display: inline-block;
    width: 24px;
    height: 8px;
  }
  .pts {
    width: 56px;
    flex: none;
    text-align: right;
    font-size: 30px;
    line-height: 36px;
    font-weight: 700;
  }
  @media (max-width: 640px) {
    .col {
      text-align: center;
    }
    .trend-title,
    .cat-title {
      margin: 0 0 12px;
      text-align: center;
    }
  }
`;

const GaugeBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 320 / 256;
  margin: 0 auto;
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
  .value {
    position: absolute;
    bottom: 15.6%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: baseline;
    font-weight: 700;
  }
  .score {
    font-size: 60px;
    line-height: 1;
  }
  .of {
    margin-left: 4px;
    font-size: 14px;
    line-height: 20px;
    color: #6b7280;
  }
`;

const ChartBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 320 / 256;
  margin: 0 auto;
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const Axis = styled.span`
  position: absolute;
  line-height: 1;
  color: #666666;
  white-space: nowrap;
  pointer-events: none;
`;

const Tip = styled.div`
  position: absolute;
  transform: translate(-50%, calc(-100% - 8px));
  background: rgba(0, 0, 0, 0.8);
  color: #ffffff;
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 8px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
`;

export default DashboardLocationView;
